import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PiButton from "../../components/PiButton";
import {
  EditorColumnFieldGroup,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";
import {
  queueSequenceCanvasCommand,
  selectSequenceCanvasSelection,
} from "../../app/slices/GlobalSlice";

function SequenceActionButton({
  label,
  tooltip,
  icon,
  onClick,
  disabled = false,
  testId,
}: {
  label: string;
  tooltip: string;
  icon: JSX.Element;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <Tooltip title={tooltip} arrow placement="right">
      <div className="w-full h-[36px]" data-testid={testId}>
        <PiButton
          variant="piBlueOutlined"
          onClick={onClick}
          disabled={disabled}
        >
          <div className="flex items-center justify-center gap-2 px-2 text-[13px]">
            {icon}
            <span className="truncate">{label}</span>
          </div>
        </PiButton>
      </div>
    </Tooltip>
  );
}

export default function SequenceCanvasActionsPanel(props: {
  onApplyLayout?: () => void;
}) {
  const dispatch = useDispatch();
  const { nodeCount, cuttableCount } = useSelector(
    selectSequenceCanvasSelection,
  ) ?? {
    nodeCount: 0,
    cuttableCount: 0,
  };
  const hasNodeSelection = nodeCount > 0;
  const hasCopyableSelection = (cuttableCount ?? 0) > 0;
  return (
    <EditorColumnSection
      title="Sequence Actions"
      icon={<BoltOutlinedIcon fontSize="inherit" />}
      defaultExpanded
      testId="section-Sequence Actions"
    >
      <EditorColumnFieldGroup>
        <div className="grid grid-cols-2 gap-2">
          <SequenceActionButton
            label="Format"
            tooltip="Format (Alt+F)"
            icon={<AutoAwesomeIcon fontSize="small" />}
            onClick={() => dispatch(queueSequenceCanvasCommand("formatLayout"))}
            testId="sequence-action-format"
          />
          <SequenceActionButton
            label="Text Note"
            tooltip="Add Text Note (Alt+T)"
            icon={<StickyNote2OutlinedIcon fontSize="small" />}
            onClick={() =>
              dispatch(queueSequenceCanvasCommand("createTextbox"))
            }
            testId="sequence-action-add-text-note"
          />
          <SequenceActionButton
            label="Comment Box"
            tooltip="Add Comment Box (Alt+C)"
            icon={<CommentOutlinedIcon fontSize="small" />}
            onClick={() =>
              dispatch(queueSequenceCanvasCommand("createCommentZone"))
            }
            disabled={!hasNodeSelection}
            testId="sequence-action-add-comment-box"
          />
          <SequenceActionButton
            label="Copy"
            tooltip="Copy (Ctrl/Cmd+C)"
            icon={<ContentCopyOutlinedIcon fontSize="small" />}
            onClick={() =>
              dispatch(queueSequenceCanvasCommand("copySelection"))
            }
            disabled={!hasCopyableSelection}
            testId="sequence-action-copy"
          />
          <SequenceActionButton
            label="Cut"
            tooltip="Cut (Ctrl/Cmd+X)"
            icon={<ContentCutOutlinedIcon fontSize="small" />}
            onClick={() => dispatch(queueSequenceCanvasCommand("cutSelection"))}
            disabled={(cuttableCount ?? 0) === 0}
            testId="sequence-action-cut"
          />
          <SequenceActionButton
            label="Paste"
            tooltip="Paste (Ctrl/Cmd+V)"
            icon={<ContentPasteOutlinedIcon fontSize="small" />}
            onClick={() =>
              dispatch(queueSequenceCanvasCommand("pasteSelection"))
            }
            testId="sequence-action-paste"
          />
          <SequenceActionButton
            label="Duplicate"
            tooltip="Duplicate (Ctrl/Cmd+D)"
            icon={<FileCopyOutlinedIcon fontSize="small" />}
            onClick={() =>
              dispatch(queueSequenceCanvasCommand("duplicateSelection"))
            }
            disabled={!hasCopyableSelection}
            testId="sequence-action-duplicate"
          />
        </div>
      </EditorColumnFieldGroup>
    </EditorColumnSection>
  );
}
